import React, { useEffect } from 'react';
import { useBattery } from '../hooks/useBattery';
import { useTranslation } from '../hooks/useTranslation';
import * as MqttTelemetry from '../services/MqttTelemetry';
import { speak } from '@caro/shared/utils';
import type { Language } from '../i18n/types';

/** Map app language codes to BCP-47 speech synthesis language tags */
const SPEECH_LANG_MAP: Record<Language, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  fr: 'fr-FR',
  de: 'de-DE',
};

/**
 * Floating battery warning overlay.
 * Shows at the top of the screen when:
 * - Battery drops below 20% (not charging)
 * - Battery is above 80% and still charging
 *
 * Also sends MQTT alerts and speaks low-battery warning aloud.
 */
export function BatteryWarning() {
  const {
    battery, showLowWarning, showHighWarning,
    dismissLow, dismissHigh, setOnLowBattery, setOnHighBattery,
  } = useBattery();
  const { lang, t } = useTranslation();

  // Wire MQTT alerts + TTS on threshold crossings
  useEffect(() => {
    setOnLowBattery((level: number) => {
      console.log(`[Battery] Low threshold hit: ${level}% — sending MQTT alert + TTS`);

      // MQTT alert
      MqttTelemetry.sendAlert({
        type: 'battery_low',
        severity: 'warning',
        message: `Battery at ${level}%, not charging`,
        service: 'battery',
      });

      // Spoken warning for elderly user.
      // Battery alerts are informational, not safety-critical at the
      // speech level — swallow rejection (no UI surface).
      speak(t.battery.lowSpoken, { lang: SPEECH_LANG_MAP[lang] || 'en-US' }).catch((err) => {
        console.warn('[BatteryWarning] speak() failed:', err);
      });
    });

    setOnHighBattery((level: number) => {
      console.log(`[Battery] High threshold hit: ${level}% — sending MQTT alert`);

      // MQTT alert (no TTS for high battery — not urgent)
      MqttTelemetry.sendAlert({
        type: 'battery_high',
        severity: 'warning',
        message: `Battery at ${level}%, still charging`,
        service: 'battery',
      });
    });
  }, [setOnLowBattery, setOnHighBattery, t, lang]);

  // Auto-hide the warning after 10 seconds once it becomes visible.
  useEffect(() => {
    if (!showLowWarning && !showHighWarning) return;
    const timer = setTimeout(() => {
      if (showLowWarning) dismissLow();
      if (showHighWarning) dismissHigh();
    }, 10000);
    return () => clearTimeout(timer);
  }, [showLowWarning, showHighWarning, dismissLow, dismissHigh]);

  if (!battery.supported) return null;
  if (!showLowWarning && !showHighWarning) return null;

  const isLow = showLowWarning;
  const bgColor = isLow ? '#dc2626' : '#f59e0b';
  const textColor = isLow ? '#fff' : '#1a202c';
  const icon = isLow ? '\u{1F50B}' : '\u{26A1}';
  const title = isLow ? t.battery.lowTitle : t.battery.highTitle;
  const message = isLow ? t.battery.lowMessage : t.battery.highMessage;
  const onDismiss = isLow ? dismissLow : dismissHigh;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 45,
      padding: '0 16px',
      pointerEvents: 'none',
    }}>
      <div style={{
        maxWidth: 480,
        margin: '16px auto',
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: 16,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        pointerEvents: 'auto',
      }}>
        <span style={{ fontSize: 'var(--font-heading)', flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-body)', marginBottom: 2 }}>
            {title}
          </div>
          <div style={{ fontSize: 'var(--font-body)', opacity: 0.9 }}>
            {message}
          </div>
        </div>
        <button
          onClick={onDismiss}
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            minWidth: 36,
            minHeight: 36,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: textColor,
            fontSize: 'var(--font-body)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          &#10005;
        </button>
      </div>
    </div>
  );
}
